import { useState } from "react";
import { Play, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Panel, Pill } from "@/components/admin/AdminUI";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import { api } from "@/lib/api";
import { formatDate, humanize } from "@/lib/format";

export const RULES = ["member_removed", "view_once", "account_deleted", "account_banned", "selfie_closed", "quarantine_expired", "owner_force_delete", "legacy_queue"];
export const ruleHelp = (rule, s = {}) => ({
  member_removed: `Photos, intros and media a member removes: deleted after ${s.media_delete_days ?? "…"} days.`,
  view_once: `View-once photos: deleted ${s.view_once_delete_minutes ?? "…"} minutes after they're opened.`,
  account_deleted: `Files from deleted accounts: deleted after ${s.account_files_delete_days ?? "…"} days.`,
  account_banned: `Files from banned accounts: deleted after ${s.banned_files_delete_days ?? "…"} days.`,
  selfie_closed: `Verification selfies: deleted ${s.selfie_delete_days ?? "…"} days after the check is closed.`,
  quarantine_expired: "Rejected uploads: deleted when their review window ends.",
  owner_force_delete: "Files the Owner deleted by hand: removed on the next run.",
  legacy_queue: "Deletions queued before R2: finished on the next run.",
}[rule] || humanize(rule));

const sum = (o) => Object.values(o || {}).reduce((a, b) => a + (Number(b) || 0), 0);

export function RunResult({ r, testId }) {
  if (!r) return <p className="cw-muted text-sm" data-testid={testId}>Never run.</p>;
  const counts = r.dry_run ? r.would_delete : r.deleted;
  const parts = Object.entries(counts || {}).filter(([, n]) => n);
  return (
    <div className="text-sm" data-testid={testId}>
      <p><span className="font-medium">{r.dry_run ? "Dry run" : "Real run"}</span> · {formatDate(r.at)}</p>
      <p className="cw-soft mt-1">{r.dry_run ? "Would delete" : "Deleted"} {sum(counts)} file{sum(counts) === 1 ? "" : "s"}{parts.length ? `: ${parts.map(([k, n]) => `${humanize(k)} ${n}`).join(", ")}` : ""}. Held by a report: {sum(r.held)}.</p>
      {r.sample?.length > 0 && <p className="cw-muted mt-1 break-all font-mono text-xs">e.g. {r.sample.slice(0, 3).join(", ")}</p>}
    </div>
  );
}

// "Run now" (dry run) and "Run now (for real)" behind a typed DELETE.
export function RunPanel({ data, onDone }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [typed, setTyped] = useState("");
  const run = async (dry_run) => {
    setError(null);
    const r = await api("/admin/storage/run", { body: { dry_run } });
    setResult(r); onDone();
  };
  const dry = async () => { setBusy(true); try { await run(true); } catch (e) { setError(e.message); } finally { setBusy(false); } };
  return (
    <Panel className="p-6" data-testid="storage-run-panel">
      <h2 className="font-semibold">Deletion job</h2>
      {data.settings?.media_deletion_dry_run && <Pill tone="warning" className="mt-2" data-testid="storage-dry-run-mode">Settings: job runs in dry-run mode</Pill>}
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div><p className="cw-muted mb-1 text-xs">Last run</p><RunResult r={data.last_run} testId="storage-last-run" /></div>
        <div><p className="cw-muted mb-1 text-xs">Last dry run</p><RunResult r={data.last_dry_run} testId="storage-last-dry-run" /></div>
      </div>
      <div className="mt-5 flex flex-wrap gap-2">
        <Button variant="outline" onClick={dry} disabled={busy} data-testid="storage-run-dry-button"><Play />{busy ? "Running…" : "Run now (dry run)"}</Button>
        <Button variant="destructive" onClick={() => { setTyped(""); setConfirm(true); }} data-testid="storage-run-real-button"><Trash />Run now (for real)</Button>
      </div>
      {error && <p className="cw-error mt-3 text-sm" role="alert" data-testid="storage-run-error">{error}</p>}
      {result && <div className="bg-[var(--cw-surface-tertiary)] mt-4 rounded-xl p-4" data-testid="storage-run-result"><RunResult r={result} testId="storage-run-result-body" /></div>}
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} testId="storage-run-real-dialog" title="Delete files for real?" confirmLabel="Delete now"
        description="Every file that is due is deleted from storage now. Files held by an open report are kept. This can't be undone."
        confirmDisabled={typed !== "DELETE"} onConfirm={() => run(false)}>
        <Label htmlFor="st-typed">Type DELETE to confirm</Label>
        <Input id="st-typed" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="DELETE" autoComplete="off" className="mt-2 font-mono" data-testid="storage-run-real-input" />
      </ConfirmDialog>
    </Panel>
  );
}
